import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { inputCls, Field, Panel, ActiveBadge, FormActions, errMsg } from "./ui";

const EMPTY = { name: "", email: "", password: "", active: true, permissions: [] };
const PERMS = [
  ["manage_stock", "Kelola stok marketplace (isi / tambah / kurangi)"],
  ["manage_prices", "Atur minimal pembelian"],
  ["manage_demand", "Kelola Job / Demand Board (buat demand, alokasi, reputasi)"],
  ["manage_users", "Blokir / buka blokir buyer & supplier"],
  ["review_kyc", "Lihat & setujui KYC supplier (KTP/selfie)"],
  ["monitor_suppliers", "Monitoring supplier (lihat kontak Telegram/WhatsApp)"],
];

export default function StaffTab() {
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/users", { params: { role: "staff" } }).then((r) => setStaff(r.data));
  const reset2fa = async (s) => {
    if (!window.confirm(`Reset 2FA untuk ${s.name}? Staff harus memasang ulang Google Authenticator saat login berikutnya.`)) return;
    try {
      await api.patch(`/users/staff/${s.id}`, { reset_2fa: true });
      toast.success(`2FA ${s.name} direset`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  useEffect(() => { load(); }, []);
  const toggleActive = async (s) => {
    if (!window.confirm(s.active ? `Nonaktifkan ${s.name}? Staff langsung keluar dan tidak bisa login.` : `Aktifkan kembali ${s.name}?`)) return;
    try { await api.patch(`/users/staff/${s.id}`, { active: !s.active }); toast.success(s.active ? "Staff dinonaktifkan" : "Staff diaktifkan"); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Hapus akun staff ${s.name}? Akun tidak bisa login lagi dan hilang dari daftar. Riwayat pesanan & log tetap tersimpan.`)) return;
    try { await api.delete(`/users/staff/${s.id}`); toast.success("Akun staff dihapus"); if (editId === s.id) reset(); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  const reset = () => { setForm(EMPTY); setEditId(null); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) {
        const payload = { name: form.name, active: form.active, permissions: form.permissions };
        if (form.password) payload.password = form.password;
        await api.patch(`/users/staff/${editId}`, payload);
      } else {
        await api.post("/users/staff", { name: form.name, email: form.email, password: form.password });
      }
      toast.success(editId ? "Staff diperbarui" : "Akun staff dibuat");
      reset(); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  const edit = (s) => { setEditId(s.id); setForm({ name: s.name, email: s.email, password: "", active: s.active, permissions: s.permissions || [] }); };
  const togglePerm = (p) => setForm((f) => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] }));

  return (
    <>
      <Panel title={editId ? "Ubah Akun Staff" : "Tambah Akun Staff"} testId="staff-form-panel">
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Nama"><input data-testid="staff-name-input" required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Email">
            <input data-testid="staff-email-input" type="email" required disabled={!!editId} className={`${inputCls} disabled:opacity-50`} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label={editId ? "Password baru (opsional)" : "Password"}>
            <input data-testid="staff-password-input" type="password" minLength={8} required={!editId} className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </Field>
          {editId && (
            <div className="md:col-span-3">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">Izin khusus</span>
              <div className="mt-2 flex flex-wrap gap-6">
                {PERMS.map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 text-sm text-[#F5F3ED]">
                    <input data-testid={`staff-perm-${k}`} type="checkbox" className="accent-[#D4AF37]" checked={form.permissions.includes(k)} onChange={() => togglePerm(k)} /> {label}
                  </label>
                ))}
              </div>
            </div>
          )}
          {editId && (
            <label className="flex items-center gap-2 text-sm text-[#F5F3ED]">
              <input data-testid="staff-active-checkbox" type="checkbox" className="accent-[#D4AF37]" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Aktif (bisa masuk)
            </label>
          )}
          <div className={`${editId ? "md:col-span-2" : "md:col-span-3"} flex md:justify-end`}><FormActions editing={!!editId} onCancel={reset} saving={saving} testPrefix="staff" /></div>
        </form>
      </Panel>

      <Panel title="Daftar Staff" testId="staff-list-panel">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-widest text-[#A8A8AD] border-b border-[#35353B]">
            <th className="py-2">Nama</th><th className="py-2">Email</th><th className="py-2">Izin</th><th className="py-2">2FA</th><th className="py-2">Status</th><th className="py-2 text-right">Aksi</th>
          </tr></thead>
          <tbody>
            {staff.map((s) => (
              <tr key={s.id} data-testid={`staff-row-${s.id}`} className="border-b border-[#35353B]">
                <td className="py-3 text-[#F5F3ED]">{s.name}</td>
                <td className="py-3 text-[#A8A8AD]">{s.email}</td>
                <td className="py-3 text-xs text-[#F2D98D]">{(s.permissions || []).map((p) => PERMS.find(([k]) => k === p)?.[1]).join(", ") || "—"}</td>
                <td data-testid={`staff-2fa-${s.id}`} className={`py-3 text-xs ${s.totp_enabled ? "text-[#35C78A]" : "text-[#F06464]"}`}>{s.totp_enabled ? "Aktif" : "Belum aktif"}</td>
                <td className="py-3"><ActiveBadge active={s.active} /></td>
                <td className="py-3 text-right whitespace-nowrap"><button data-testid={`edit-staff-${s.id}`} onClick={() => edit(s)} className="text-[#D4AF37] hover:underline text-xs">Ubah</button>
                  {s.totp_enabled && <button data-testid={`reset-2fa-staff-${s.id}`} onClick={() => reset2fa(s)} className="text-[#F06464] hover:underline text-xs ml-4">Reset 2FA</button>}
                  <button data-testid={`toggle-active-staff-${s.id}`} onClick={() => toggleActive(s)} className="text-[#F2D98D] hover:underline text-xs ml-4">{s.active ? "Nonaktifkan" : "Aktifkan"}</button>
                  <button data-testid={`delete-staff-${s.id}`} onClick={() => remove(s)} className="text-[#F06464] hover:underline text-xs ml-4">Hapus</button></td>
              </tr>
            ))}
            {staff.length === 0 && <tr><td colSpan="6" className="py-6 text-center text-[#A8A8AD]">Belum ada staff</td></tr>}
          </tbody>
        </table>
      </Panel>
    </>
  );
}
